'use client';

import clsx from 'clsx';
import { type JSX, useState, type ForwardedRef, type ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCheckCircle, IconInfoCircle, IconWarning, IconWarningCircleFill } from '../../icons/index.js';
import { withStyleProps } from '../../with-style-props.js';
import { XButton } from '../internal/x-button/x-button.js';
import { Stack } from '../spacing/stack/stack.js';
import type { CommonProps, IconProps } from '../types.js';
import { Text } from '../typography/text.js';

const iconMap: Record<CalloutProps['tone'], (props: IconProps) => JSX.Element> = {
  info: IconInfoCircle,
  success: IconCheckCircle,
  warning: IconWarning,
  critical: IconWarningCircleFill,
};

export interface CalloutProps extends CommonProps {
  /** The title of the callout. */
  title?: string;
  /** The content of the callout. */
  children: ReactNode;
  /** The tone of the callout. */
  tone: 'info' | 'success' | 'warning' | 'critical';
  /** Optional actions to display at the bottom of the callout. Use `Button` or `LinkButton` components only. */
  actions?: ReactNode;
  /**
   * Whether the callout can be dismissed by the user.
   * @default false
   */
  isDismissible?: boolean;
  /** Callback function to be called when the dismiss button is pressed. Makes the callout controlled. */
  onDismiss?: () => void;
}

function Callout(
  {
    UNSAFE_className,
    UNSAFE_style,
    tone,
    title,
    children,
    actions,
    isDismissible = false,
    onDismiss,
    ...props
  }: CalloutProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const Icon = iconMap[tone];
  const messages = useLocalizedMessages('common');

  const [isDismissed, setIsDismissed] = useState(false);
  function handleDismiss() {
    if (onDismiss == null) {
      setIsDismissed(true);
    }
    onDismiss?.();
  }

  if (isDismissed) {
    return null;
  }

  return (
    <div {...props} ref={ref} className={clsx('cim-callout', UNSAFE_className)} style={UNSAFE_style} data-tone={tone}>
      <div className="cim-callout-icon">
        <Icon size={24} aria-hidden />
      </div>
      <div className="cim-callout-content">
        {title && (
          <Text as="h3" UNSAFE_className="cim-callout-title" variant="title-6" tone="base">
            {title}
          </Text>
        )}
        <Text as="div" UNSAFE_className="cim-callout-text" variant="medium" tone="base">
          {children}
        </Text>
        {actions && (
          <Stack gap={8} direction="horizontal" wrap UNSAFE_className="cim-callout-actions">
            {actions}
          </Stack>
        )}
      </div>
      {(isDismissible || onDismiss != null) && (
        <div className="cim-callout-close">
          <XButton onPress={handleDismiss} aria-label={messages.format('dismiss')} />
        </div>
      )}
    </div>
  );
}

/**
 * Displays an inline message banner that highlights important information.
 *
 * See [callout usage guidelines](https://ui.cimpress.io/components/callout/).
 */
const _Callout = withStyleProps(forwardRef(Callout), 'Callout');

export { _Callout as Callout };
